import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'

const AccountSetupScreen = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, signUp, signIn } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Form data
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [trackingMode, setTrackingMode] = useState('cycle')
  const [lastPeriodDate, setLastPeriodDate] = useState('')
  const [cycleLength, setCycleLength] = useState(28)
  const [isSignIn, setIsSignIn] = useState(false)

  // Check if coming from premium purchase
  const fromPremium = searchParams.get('premium') === 'true'
  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    // If user is already authenticated, skip to step 2
    if (user) {
      setCurrentStep(2)
      setName(user.user_metadata?.full_name || '')
      setEmail(user.email)
      
      // Load existing preferences
      const storedMode = localStorage.getItem('trackingMode')
      const storedDate = localStorage.getItem('lastPeriodDate')
      const storedLength = localStorage.getItem('cycleLength')
      
      if (storedMode) setTrackingMode(storedMode)
      if (storedDate) setLastPeriodDate(storedDate)
      if (storedLength) setCycleLength(parseInt(storedLength))
    }

    // If coming from successful payment, mark as premium
    if (sessionId) {
      localStorage.setItem('isPremium', 'true')
      localStorage.setItem('purchaseDate', new Date().toISOString())
      localStorage.setItem('stripeSessionId', sessionId)
    }
  }, [user, sessionId])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleAuthSubmit = async () => {
    setLoading(true)
    setError('')

    // Validation
    if (!email?.trim() || !password?.trim()) {
      setError('Please fill in all fields.')
      setLoading(false)
      return
    }

    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.')
      setLoading(false)
      return
    }

    if (!isSignIn && !name?.trim()) {
      setError('Please enter your name.')
      setLoading(false)
      return
    }

    try {
      if (isSignIn) {
        const { data, error } = await signIn(email.trim(), password)
        if (error) throw error
        
        // Move to preferences step
        setCurrentStep(2)
      } else {
        const { data, error } = await signUp(email.trim(), password, {
          full_name: name.trim(),
          tracking_mode: trackingMode
        })
        if (error) throw error
        
        // Move to preferences step
        setCurrentStep(2)
      }
    } catch (error) {
      console.error('Auth error:', error)
      let errorMessage = 'Authentication failed. Please try again.'
      
      if (error.message) {
        errorMessage = error.message
        if (error.message.includes('An account with this email already exists')) {
          setIsSignIn(true)
          errorMessage = 'Account exists. Please sign in instead.'
        }
      }
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePreferencesSubmit = () => {
    // Save preferences to localStorage
    localStorage.setItem('trackingMode', trackingMode)
    
    if (trackingMode === 'cycle') {
      if (lastPeriodDate) {
        localStorage.setItem('lastPeriodDate', lastPeriodDate)
      }
      localStorage.setItem('cycleLength', cycleLength.toString())
    } else {
      // Clear cycle data if switching to moon mode
      localStorage.removeItem('lastPeriodDate')
      localStorage.removeItem('cycleLength')
    }

    // Move to final step
    setCurrentStep(3)
  }

  const handleComplete = () => {
    const isPremium = localStorage.getItem('isPremium') === 'true'
    
    if (isPremium) {
      navigate('/premium-dashboard')
    } else if (fromPremium) {
      navigate('/unlock')
    } else {
      navigate('/')
    }
  }

  const renderStep1 = () => (
    <div>
      <div className="text-center mb-8">
        <h1 className="italiana-font text-3xl text-black mb-4">
          {isSignIn ? 'Welcome Back! 🌙' : 'Create Your Account 🌙'}
        </h1>
        <p className="text-sm text-gray-600">
          {isSignIn 
            ? 'Sign in to access your personalized cycle guidance' 
            : 'Set up your secure account to save your cycle data'
          }
        </p>
        {fromPremium && (
          <div className="bg-gold bg-opacity-10 p-4 rounded-lg mt-4">
            <p className="text-sm text-gold font-medium">
              🎉 Premium access confirmed! Complete setup to access all features.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {!isSignIn && (
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
              placeholder="Enter your first name"
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-black mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
            placeholder="your@email.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-black mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
            placeholder={isSignIn ? "Enter your password" : "Create a secure password"}
            minLength={6}
            required
          />
          {!isSignIn && (
            <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <motion.button
          onClick={handleAuthSubmit}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{isSignIn ? 'Signing In...' : 'Creating Account...'}</span>
            </div>
          ) : (
            isSignIn ? 'Sign In →' : 'Create Account →'
          )}
        </motion.button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setIsSignIn(!isSignIn)
              setError('')
            }}
            className="text-sm text-gray-500 hover:text-black transition-colors"
          >
            {isSignIn 
              ? "Don't have an account? Sign up" 
              : 'Already have an account? Sign in'
            }
          </button>
        </div>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div>
      <div className="text-center mb-8">
        <h1 className="italiana-font text-3xl text-black mb-4">
          Personalize Your Cycle Guidance 🌙
        </h1>
        <p className="text-sm text-gray-600">
          Choose how you'd like to track your natural rhythm for personalized guidance
        </p>
      </div>

      <div className="space-y-6">
        {/* Tracking Mode Selection */}
        <div>
          <h3 className="text-sm font-medium text-black mb-4">
            How would you like to track your natural rhythm?
          </h3>
          <div className="space-y-3">
            <motion.button
              type="button"
              onClick={() => setTrackingMode('cycle')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full p-4 text-left border rounded-lg transition-all ${
                trackingMode === 'cycle' 
                  ? 'border-gold bg-gold bg-opacity-10' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                  trackingMode === 'cycle' ? 'border-gold bg-gold' : 'border-gray-300'
                }`} />
                <div>
                  <h4 className="font-medium text-black">🩸 Menstrual Cycle + Moon Phases</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Track your period to get precise, personalized guidance based on your exact cycle day
                  </p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => setTrackingMode('moon')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full p-4 text-left border rounded-lg transition-all ${
                trackingMode === 'moon' 
                  ? 'border-gold bg-gold bg-opacity-10' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                  trackingMode === 'moon' ? 'border-gold bg-gold' : 'border-gray-300'
                }`} />
                <div>
                  <h4 className="font-medium text-black">🌙 Moon Phases Only</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Perfect if you don't menstruate, have irregular cycles, or prefer lunar guidance
                  </p>
                </div>
              </div>
            </motion.button>
          </div>
        </div>

        {/* Cycle Details (if cycle tracking) */}
        {trackingMode === 'cycle' && (
          <>
            <div>
              <label className="block text-sm font-medium text-black mb-3">
                When did your last period start?
              </label>
              <input
                type="date"
                value={lastPeriodDate}
                onChange={(e) => setLastPeriodDate(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-3">
                What's your typical cycle length?
              </label>
              <select
                value={cycleLength}
                onChange={(e) => setCycleLength(parseInt(e.target.value))}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
              >
                {[21,22,23,24,25,26,27,28,29,30,31,32,33,34,35].map(length => (
                  <option key={length} value={length}>
                    {length} days
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {trackingMode === 'moon' && (
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-medium text-black mb-2">🌙 Moon Phase Guidance</h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              You'll receive personalized actions based on the current moon phase and its natural energy patterns. 
              Perfect for connecting with lunar rhythms and seasonal cycles.
            </p>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={() => setCurrentStep(1)}
            className="flex-1 border border-gray-300 text-gray-700 py-4 px-6 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            ← Back
          </button>
          <motion.button
            onClick={handlePreferencesSubmit}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={trackingMode === 'cycle' && !lastPeriodDate}
            className="flex-1 gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Next →
          </motion.button>
        </div>
      </div>
    </div>
  )

  const renderStep3 = () => {
    const isPremium = localStorage.getItem('isPremium') === 'true'
    
    return (
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="mb-8"
        >
          <div className="w-24 h-24 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl text-white">✨</span>
          </div>
        </motion.div>

        <h1 className="italiana-font text-3xl text-black mb-4">
          {isPremium ? 'Premium Account Ready! 🌙' : 'Account Created Successfully! 🌙'}
        </h1>

        <p className="text-gray-600 mb-8 leading-relaxed">
          {isPremium 
            ? 'Your premium account is set up and ready! You now have access to all features including complete guidance, smart coaching, and calendar planning.'
            : 'Your account has been created successfully. You can now access personalized cycle guidance and save your data securely.'
          }
        </p>

        {isPremium && (
          <div className="bg-gold bg-opacity-10 p-6 rounded-lg mb-8 text-left">
            <h3 className="font-semibold text-black mb-4 text-center">
              🎉 Your Premium Features:
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <span className="gold-accent text-lg">✓</span>
                <span>Complete daily guidance for every life scenario</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="gold-accent text-lg">✓</span>
                <span>Smart chat coaching for personalized alternatives</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="gold-accent text-lg">✓</span>
                <span>Full cycle calendar with planning tools</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="gold-accent text-lg">✓</span>
                <span>Personal insights and pattern tracking</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="gold-accent text-lg">✓</span>
                <span>Secure cloud sync across all devices</span>
              </div>
            </div>
          </div>
        )}

        <motion.button
          onClick={handleComplete}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity mb-4"
        >
          {isPremium 
            ? 'Enter Premium Dashboard →' 
            : 'Start Using Cycle CEO →'
          }
        </motion.button>

        {!isPremium && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-3">
              Want complete guidance for every scenario?
            </p>
            <button
              onClick={() => navigate('/unlock')}
              className="text-sm gold-accent font-medium hover:underline"
            >
              Upgrade to Premium →
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Back button (only on step 1) */}
        {currentStep === 1 && (
          <div className="text-center mb-6">
            <button
              onClick={() => navigate(fromPremium ? '/unlock' : '/')}
              className="text-sm text-gray-500 hover:text-black transition-colors"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${currentStep >= 1 ? 'bg-gold' : 'bg-gray-300'}`} />
            <div className="w-8 h-0.5 bg-gray-300" />
            <div className={`w-3 h-3 rounded-full ${currentStep >= 2 ? 'bg-gold' : 'bg-gray-300'}`} />
            <div className="w-8 h-0.5 bg-gray-300" />
            <div className={`w-3 h-3 rounded-full ${currentStep >= 3 ? 'bg-gold' : 'bg-gray-300'}`} />
          </div>
        </div>

        {/* Step Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </motion.div>

        {/* Privacy Note */}
        {currentStep < 3 && (
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              🔒 Your data is encrypted and secure. We never share your personal information.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default AccountSetupScreen